/**
 * Документы члена фирмы: список и открытие файла подписанной ссылкой.
 *
 * Запись членства приходит извне, разобранная маршрутизатором: экран ничего не
 * выбирает — он берёт данные ему идентификаторы и спрашивает сервер.
 *
 * Единственный путь к файлу — **временная подписанная ссылка**. Ссылка просится
 * в момент нажатия, а не при отрисовке списка: список из пяти документов при
 * отрисовке даёт ноль обращений, потому что срок у ссылки свой и начинается он
 * в момент выдачи. Открывается **ровно** значение `url` из ответа: оно
 * непрозрачно, и ни склейки с базовым адресом, ни заголовка входа на этом
 * открытии нет. Годность ссылки клиент не обсуждает — `expires_at` не
 * сравнивается ни с чем, решает сервер.
 *
 * Полей файла — имени, размера, MIME, пути — на экране нет ни одного: их нет в
 * схеме намеренно, и показать такое поле значило бы показать выдуманное.
 *
 * Тип документа показан не будет: перечня подписей для него словарь имён блока
 * не объявлял, а машинное значение сервера, поставленное на экран, — это
 * ровно то, чего не допускает правило неизвестного значения перечисления.
 *
 * Состояние «пусто» принадлежит **списку**, а не экрану, — то же правило, что
 * на профиле фирмы, и записано оно здесь потому, что правило принадлежит
 * болезни, а не экрану. Пустота списка есть свойство **пришедшего ответа**:
 * сотрудник без документов — обычное состояние, а не отсутствие экрана, и
 * заголовку в нём пропадать не с чего. Экран целиком остаётся за исходом, в
 * котором ответа нет вовсе: за незавершённым чтением и за отказом.
 */

import { ApiFailure } from '../api/client';
import type { ApiClient } from '../api/client';
import type { Document } from '../api/contract';
import { documentStatusStringKey } from '../i18n';
import type { Strings } from '../i18n';
import { enumLabel, placeState, screenStateFor } from './state';
import type { ScreenState } from './state';

export interface MemberDocumentsScreenOptions {
  readonly client: ApiClient;
  readonly strings: Strings;
  readonly companyId: string;
  readonly membershipId: string;
}

export interface MemberDocumentsScreen {
  readonly element: HTMLElement;
  readonly state: ScreenState | null;
  readonly documents: readonly Document[];
  load(): Promise<void>;
  /** Просит ссылку у сервера и открывает пришедшее значение как есть. */
  openDocument(documentId: string): Promise<void>;
}

export function createMemberDocumentsScreen(
  options: MemberDocumentsScreenOptions,
): MemberDocumentsScreen {
  const { client, strings, companyId, membershipId } = options;

  const element = document.createElement('section');
  const heading = document.createElement('h1');
  const list = document.createElement('ul');

  element.className = 'screen screen-member-documents';
  heading.textContent = strings.member_documents_title;

  let state: ScreenState | null = 'loading';
  let documents: readonly Document[] = [];
  /**
   * Пришёл ли ответ сервера.
   *
   * Пустой список и отсутствие ответа неразличимы по самому списку — он пуст в
   * обоих случаях, — а показывать их надо по-разному: у первого есть экран, у
   * второго нет ничего. Это и есть та величина, которую пустой список заменял
   * собой.
   */
  let answered = false;

  function row(entry: Document): HTMLElement {
    const node = document.createElement('li');
    const line = document.createElement('span');
    const download = document.createElement('button');

    node.className = 'document-row';
    line.textContent = [
      enumLabel(strings, documentStatusStringKey(entry.status)),
      entry.number ?? '',
      entry.expires_at ?? '',
    ].join(' ');

    download.type = 'button';
    download.className = 'action';
    download.textContent = strings.document_download;
    download.addEventListener('click', () => {
      void openDocument(entry.id);
    });

    node.append(line, download);

    return node;
  }

  function render(): void {
    const rows: HTMLElement[] = [];

    for (const entry of documents) {
      rows.push(row(entry));
    }

    list.replaceChildren(...rows);
    // Правило места состояния — одна функция на все экраны (долг `TD-66`):
    // ответа нет — показывать нечего вовсе; ответ пустой — состояние встаёт на
    // место **списка**, и заголовок экрана остаётся.
    placeState(element, { state, answered, frame: [heading], list }, strings);
  }

  async function load(): Promise<void> {
    state = screenStateFor({ kind: 'pending' });
    documents = [];
    answered = false;
    render();

    try {
      const received = await client.listMemberDocuments(companyId, membershipId);

      documents = received;
      answered = true;
      state = screenStateFor({ kind: 'done', empty: received.length === 0 });
    } catch (error) {
      documents = [];
      answered = false;
      state = screenStateFor({
        kind: 'failed',
        code: error instanceof ApiFailure ? error.code : null,
      });
    }

    render();
  }

  async function openDocument(documentId: string): Promise<void> {
    try {
      const link = await client.issueDocumentLink(documentId);

      // Ровно значение из ответа: оно непрозрачно, и собрать его руками
      // нельзя. Срок ссылки решает сервер — сравнивать его здесь не с чем.
      window.open(link.url);
    } catch {
      // Отказ выдачи ссылки — это отказ **действия**, а не экрана: список уже
      // показан, и «нет доступа» сказало бы о самом документе то, чего сервер
      // здесь не сообщал. Молчание же оставило бы нажатие без последствий.
      state = 'error';
      render();
    }
  }

  render();

  return {
    element,

    get state(): ScreenState | null {
      return state;
    },

    get documents(): readonly Document[] {
      return documents;
    },

    load,
    openDocument,
  };
}
